import { CloseCircleOutlined } from "@ant-design/icons";
import { Button, ButtonProps, Col, ConfigProvider, Modal, Row } from "antd";
import { isEmpty } from "lodash";
import React from "react";
import { colorFormat } from "utils/format/ColorFormat";
import { strings } from "utils/localizedStrings";

interface ModalButtonProps {
  color?: string;
  children?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLElement>;
  antdProps?: ButtonProps;
}

export interface ModalCustomFooter {
  hidden?: boolean;
  props?: ModalButtonProps;
}

interface IBaseModalProps {
  visible?: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
  title?: React.ReactNode;
  showCloseFooter?: boolean;
  width?: number;
  loading?: boolean;
  customFooter?: ModalCustomFooter[];
}

const BaseModal = (props: IBaseModalProps) => {
  const {
    visible,
    onClose,
    children,
    title,
    width,
    showCloseFooter = true,
    loading,
    customFooter
  } = props;

  const ModalButton = (props: ModalButtonProps) => {
    const { color, antdProps, children, onClick } = props;
    return (
      <ConfigProvider theme={{ token: { colorPrimary: color || colorFormat.Blue } }}>
        <Button
          type="primary"
          size="large"
          onClick={onClick}
          {...antdProps}
          className={`xl:h-[50px] h-[40px] font-[600] ${antdProps?.className}`}
        >
          {children}
        </Button>
      </ConfigProvider>
    )
  }

  const renderCustomFooter = () => {
    return (
      <div
        className={`w-full flex justify-center sm:justify-center gap-x-4 overflow-scroll`}
        style={{ scrollbarWidth: "none" }}
      >
        {customFooter?.map((footer, index) => (
          footer?.hidden === true ? null : <ModalButton
            color={footer.props?.color}
            children={footer.props?.children}
            onClick={footer.props?.onClick}
            antdProps={footer.props?.antdProps}
            key={index}
          />
        ))}
      </div>
    )
  }

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      destroyOnHidden
      maskClosable={false}
      loading={loading}
      footer={!isEmpty(customFooter) ? renderCustomFooter() : (showCloseFooter &&
        <Row gutter={[8, 8]} justify={"start"}>
          <Col
            span={24}
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ModalButton onClick={onClose}>
              {strings.GlobalLabels.Close}
            </ModalButton>
          </Col>
        </Row>
      )}
      closeIcon={null}
      width={width}
      centered
      styles={{
        body: {
          overflowY: "auto",
          maxHeight: "calc(100vh - 200px)",
          scrollbarWidth: "none",
          overflowX: "hidden",
        },
        content: {
          padding: "0 6px 6px 6px",
        },
        header: {
          padding: 5,
          width: "100%",
        },
        footer: {
          padding: "0 6px",
        }
      }}
      title={
        <div className="flex py-3 px-2 justify-between w-full border-b-[1px] border-b-[#E5E5E5]">
          <div className="w-1 h-1"></div>
          <span className="font-bold">{title}</span>
          <CloseCircleOutlined
            onClick={onClose}
            style={{ color: "black", fontSize: "20px" }}
          />
        </div>
      }
    >
      {children}
    </Modal>
  );
}

export default BaseModal